import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Play,
  ArrowRight,
  Check,
  X,
  Lightbulb,
  RotateCcw,
  Award,
  TrendingUp,
  Target,
  Users,
  Rocket,
  Clock,
  Trophy
} from 'lucide-react';
import type { SimulationState } from '../types/workshops';
import { degapSimulationScenario, degapSimulationSteps } from '../data/workshopScenarios';
import { workshopProgressTracker } from '../lib/workshopProgressTracker';
import type { WorkshopProgress } from '../lib/workshopProgressTracker';

interface DEGAPSimulationProps {
  onComplete?: (finalScore: number, state: SimulationState) => void;
}

export const DEGAPSimulation: React.FC<DEGAPSimulationProps> = ({ onComplete }) => {
  const workshopId = 'degap-simulation';
  const workshopName = 'DEGAP Prioritization Simulation';
  
  const [hasStarted, setHasStarted] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [score, setScore] = useState(0);
  const [savedProgress, setSavedProgress] = useState<WorkshopProgress | null>(null);
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [attempts, setAttempts] = useState(workshopProgressTracker.getAttempts(workshopId));
  const [bestScore, setBestScore] = useState(workshopProgressTracker.getBestScore(workshopId));

  const currentStep = degapSimulationSteps[currentStepIndex];
  const totalSteps = degapSimulationSteps.length;
  const progress = ((currentStepIndex + (showFeedback ? 1 : 0)) / totalSteps) * 100;

  // Load saved progress on mount
  useEffect(() => {
    const saved = workshopProgressTracker.getProgress(workshopId);
    if (saved && saved.status === 'in-progress') {
      setSavedProgress(saved);
      setShowResumePrompt(true);
    }
  }, []);

  // Save progress after each step
  useEffect(() => {
    if (hasStarted && !showFeedback) {
      workshopProgressTracker.saveProgress({
        workshopId,
        workshopName,
        currentStepIndex,
        selectedOptions,
        score,
        completedSteps,
        startedAt: savedProgress?.startedAt || new Date().toISOString(),
        lastUpdatedAt: new Date().toISOString(),
        status: 'in-progress',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- persistence effect: adding the written fields would re-run the save on every answer
  }, [currentStepIndex, hasStarted, showFeedback]);

  const handleStart = () => {
    setHasStarted(true);
    setShowResumePrompt(false);
  };

  const handleResume = () => {
    if (!savedProgress) return;
    setCurrentStepIndex(savedProgress.currentStepIndex);
    setSelectedOptions(savedProgress.selectedOptions);
    setScore(savedProgress.score);
    setCompletedSteps(savedProgress.completedSteps);
    setHasStarted(true);
    setShowResumePrompt(false);
  };

  const handleStartFresh = () => {
    workshopProgressTracker.resetProgress(workshopId);
    setSavedProgress(null);
    setShowResumePrompt(false);
    handleStart();
  };

  const handleSelectOption = (optionId: string) => {
    setSelectedOption(optionId);
  };

  const handleSubmit = () => {
    if (!selectedOption || !currentStep) return;

    setShowFeedback(true);
    
    // Update state
    const newSelectedOptions = {
      ...selectedOptions,
      [currentStep.id]: selectedOption,
    };
    setSelectedOptions(newSelectedOptions);

    // Calculate score for this step
    const feedback = currentStep.feedbackMap[selectedOption];
    const stepScore = feedback.rating === 'excellent' ? 100
      : feedback.rating === 'good' ? 75
      : feedback.rating === 'okay' ? 50
      : 25;
    
    setScore(prev => prev + stepScore);
  };

  const handleNext = () => {
    if (!currentStep) return;

    setCompletedSteps(prev => [...prev, currentStep.id]);
    
    if (currentStepIndex < degapSimulationSteps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
      setSelectedOption(null);
      setShowFeedback(false);
    } else {
      // Simulation complete
      const finalScore = Math.round(score / totalSteps);
      
      // Save as completed attempt
      const stepResults = degapSimulationSteps.map((step) => {
        const optionId = selectedOptions[step.id];
        const feedback = step.feedbackMap[optionId];
        const stepScore = feedback.rating === 'excellent' ? 100
          : feedback.rating === 'good' ? 75
          : feedback.rating === 'okay' ? 50
          : 25;
        
        return {
          stepId: step.id,
          stage: step.stage,
          selectedOption: optionId,
          score: stepScore,
        };
      });
      
      workshopProgressTracker.completeWorkshop(
        workshopId,
        workshopName,
        finalScore,
        stepResults
      );
      
      // Update local state
      setAttempts(workshopProgressTracker.getAttempts(workshopId));
      setBestScore(workshopProgressTracker.getBestScore(workshopId));
      
      const state: SimulationState = {
        scenarioId: degapSimulationScenario.id,
        currentStepId: currentStep.id,
        completedSteps: [...completedSteps, currentStep.id],
        selectedOptions,
        score: finalScore,
        startedAt: new Date(),
        completedAt: new Date(),
      };
      
      if (onComplete) {
        onComplete(finalScore, state);
      }
    }
  };

  const handleRestart = () => {
    workshopProgressTracker.resetProgress(workshopId);
    setHasStarted(false);
    setCurrentStepIndex(0);
    setSelectedOption(null);
    setShowFeedback(false);
    setCompletedSteps([]);
    setSelectedOptions({});
    setScore(0);
    setSavedProgress(null);
  };

  const currentFeedback = selectedOption && currentStep ? currentStep.feedbackMap[selectedOption] : null;

  // Start Screen
  if (!hasStarted) {
    return (
      <div className="max-w-3xl mx-auto space-y-4">
        {/* Resume Prompt */}
        {showResumePrompt && savedProgress && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-amber-600 rounded-lg">
                <Clock className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-charcoal-900 mb-2">
                  Continue Where You Left Off?
                </h3>
                <p className="text-charcoal-700 mb-4">
                  You have progress saved from{' '}
                  {new Date(savedProgress.lastUpdatedAt).toLocaleDateString()} at step{' '}
                  {savedProgress.currentStepIndex + 1} of {totalSteps}.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={handleResume}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg transition-colors"
                  >
                    <Play className="h-4 w-4" />
                    Resume Progress
                  </button>
                  <button
                    onClick={handleStartFresh}
                    className="px-4 py-2 bg-white hover:bg-charcoal-50 text-charcoal-700 font-semibold rounded-lg border-2 border-charcoal-300 transition-colors"
                  >
                    Start Fresh
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Card */}
        {(attempts.length > 0 || bestScore !== null) && (
          <div className="bg-gradient-to-r from-sage-50 to-primary-50 rounded-xl p-4 border border-sage-200">
            <div className="flex items-center gap-6 justify-center text-sm">
              {bestScore !== null && (
                <div className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-amber-600" />
                  <span className="text-charcoal-700">
                    Best Score: <strong className="text-amber-700">{bestScore}%</strong>
                  </span>
                </div>
              )}
              {attempts.length > 0 && (
                <div className="text-charcoal-600">
                  {attempts.length} attempt{attempts.length > 1 ? 's' : ''} completed
                </div>
              )}
            </div>
          </div>
        )}

        {/* Main Intro Card */}
        <div className="bg-gradient-to-br from-primary-50 to-sage-50 rounded-xl border-2 border-primary-300 p-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 bg-primary-600 rounded-lg">
              <Play className="h-8 w-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-charcoal-900 mb-2">
                {degapSimulationScenario.title}
              </h2>
              <div className="flex items-center gap-4 text-sm text-charcoal-600">
                <span className="flex items-center gap-1">
                  <TrendingUp className="h-4 w-4" />
                  {degapSimulationScenario.difficulty}
                </span>
                <span>⏱️ {degapSimulationScenario.estimatedTime} min</span>
                <span className="px-2 py-1 bg-sage-200 text-sage-800 rounded text-xs font-semibold">
                  {degapSimulationScenario.context}
                </span>
              </div>
            </div>
          </div>

          <p className="text-charcoal-700 mb-6 leading-relaxed">
            {degapSimulationScenario.description}
          </p>

          <div className="bg-white rounded-lg p-6 mb-6">
            <h3 className="font-bold text-charcoal-900 mb-3">Learning Objectives:</h3>
            <ul className="space-y-2">
              {degapSimulationScenario.learningObjectives.map((objective, idx) => (
                <li key={idx} className="flex items-start gap-2 text-charcoal-700">
                  <Check className="h-5 w-5 text-sage-600 flex-shrink-0 mt-0.5" />
                  <span>{objective}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-center justify-between">
            <Link
              to="/elevation/entry/pf.degap.stages"
              className="text-primary-600 hover:text-primary-800 text-sm underline"
            >
              Review DEGAP Framework →
            </Link>
            {!showResumePrompt && (
              <button
                onClick={handleStart}
                className="flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors shadow-md"
              >
                <Play className="h-5 w-5" />
                Start Simulation
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Simulation Complete Screen
  if (completedSteps.length === totalSteps) {
    const finalScore = Math.round(score / totalSteps);
    const performance = finalScore >= 90 ? 'Excellent'
      : finalScore >= 75 ? 'Good'
      : finalScore >= 60 ? 'Fair'
      : 'Needs Improvement';

    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-gradient-to-br from-amber-50 to-sage-50 rounded-xl border-2 border-amber-300 p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-amber-600 text-white rounded-full mb-4">
              <Award className="h-10 w-10" />
            </div>
            <h2 className="text-3xl font-bold text-charcoal-900 mb-2">
              Simulation Complete!
            </h2>
            <p className="text-charcoal-600">
              You\'ve navigated through all five DEGAP stages
            </p>
          </div>

          <div className="bg-white rounded-lg p-6 mb-6">
            <div className="text-center mb-4">
              <div className="text-6xl font-bold text-amber-600 mb-2">
                {finalScore}%
              </div>
              <div className="text-lg font-semibold text-charcoal-700">
                {performance}
              </div>
            </div>

            <div className="grid grid-cols-5 gap-4 mt-6">
              {degapSimulationSteps.map((step) => {
                const optionId = selectedOptions[step.id];
                const feedback = optionId ? step.feedbackMap[optionId] : null;
                const stepScore = feedback?.rating === 'excellent' ? 100
                  : feedback?.rating === 'good' ? 75
                  : feedback?.rating === 'okay' ? 50
                  : 25;

                return (
                  <div key={step.id} className="text-center">
                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full mb-2 ${
                      feedback?.rating === 'excellent' ? 'bg-sage-600 text-white'
                      : feedback?.rating === 'good' ? 'bg-primary-600 text-white'
                      : feedback?.rating === 'okay' ? 'bg-amber-600 text-white'
                      : 'bg-coral-600 text-white'
                    }`}>
                      {feedback?.rating === 'excellent' || feedback?.rating === 'good' ? (
                        <Check className="h-6 w-6" />
                      ) : (
                        <X className="h-6 w-6" />
                      )}
                    </div>
                    <div className="text-xs font-semibold text-charcoal-700">{step.stage}</div>
                    <div className="text-xs text-charcoal-500">{stepScore}%</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-center gap-4 mb-8">
            <button
              onClick={handleRestart}
              className="flex items-center gap-2 px-6 py-3 bg-white hover:bg-charcoal-50 text-charcoal-700 font-semibold border-2 border-charcoal-300 rounded-lg transition-colors"
            >
              <RotateCcw className="h-5 w-5" />
              Try Again
            </button>
            <Link
              to="/elevation/entry/pf.degap.stages"
              className="flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors"
            >
              Review Framework
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>

          {/* Past Attempts */}
          {attempts.length > 0 && (
            <div className="border-t-2 border-gray-200 pt-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-charcoal-900 flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-amber-600" />
                  Your Progress History
                </h3>
                {bestScore !== null && bestScore === finalScore && (
                  <span className="px-3 py-1 bg-amber-600 text-white text-xs font-bold rounded-full">
                    🎉 New Best Score!
                  </span>
                )}
              </div>
              <div className="bg-white rounded-lg border border-charcoal-200 overflow-hidden">
                <div className="max-h-48 overflow-y-auto">
                  {attempts.map((attempt, idx) => (
                    <div
                      key={attempt.id}
                      className={`flex items-center justify-between p-3 border-b border-charcoal-100 last:border-b-0 ${
                        idx === 0 ? 'bg-amber-50' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="text-xs font-mono text-charcoal-500 w-4">
                          #{attempts.length - idx}
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-charcoal-900">
                            {attempt.finalScore}%
                          </div>
                          <div className="text-xs text-charcoal-600">
                            {new Date(attempt.completedAt).toLocaleDateString()} at{' '}
                            {new Date(attempt.completedAt).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                      </div>
                      {attempt.finalScore === bestScore && (
                        <Trophy className="h-4 w-4 text-amber-600" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Next Steps CTAs */}
          <div className="border-t-2 border-gray-200 pt-6">
            <h3 className="text-lg font-bold text-charcoal-900 mb-4 flex items-center gap-2">
              <Rocket className="h-5 w-5 text-primary-600" />
              What's Next?
            </h3>
            <div className="grid md:grid-cols-2 gap-3">
              <Link
                to="/elevation/outcomes"
                className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors group"
              >
                <Target className="h-5 w-5 text-amber-600" />
                <div className="flex-1">
                  <div className="font-semibold text-charcoal-900 text-sm group-hover:text-amber-700">Apply to an Outcome</div>
                  <div className="text-xs text-charcoal-600">Track DEGAP in a real project</div>
                </div>
              </Link>
              <Link
                to="/elevation/team"
                className="flex items-center gap-3 p-3 bg-sage-50 border border-sage-200 rounded-lg hover:bg-sage-100 transition-colors group"
              >
                <Users className="h-5 w-5 text-sage-600" />
                <div className="flex-1">
                  <div className="font-semibold text-charcoal-900 text-sm group-hover:text-sage-700">Read Team Stories</div>
                  <div className="text-xs text-charcoal-600">See how others use DEGAP</div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Active Simulation Step
  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold text-charcoal-700">
            Stage {currentStepIndex + 1} of {totalSteps}: {currentStep.stage}
          </div>
          <div className="text-sm text-charcoal-600">
            {Math.round(progress)}% Complete
          </div>
        </div>
        <div className="w-full bg-charcoal-200 rounded-full h-3">
          <div
            className="bg-gradient-to-r from-primary-600 to-sage-600 h-3 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-xl shadow-lg border-2 border-primary-200 overflow-hidden">
        <div className="bg-gradient-to-r from-primary-50 to-sage-50 p-6 border-b-2 border-primary-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="px-3 py-1 bg-primary-600 text-white font-bold rounded">
              {currentStep.stage}
            </div>
            <h3 className="text-xl font-bold text-charcoal-900">
              {currentStep.title}
            </h3>
          </div>
          <p className="text-charcoal-700 leading-relaxed">
            {currentStep.description}
          </p>
        </div>

        <div className="p-6">
          <div className="bg-sage-50 border-l-4 border-sage-600 p-4 mb-6">
            <p className="font-semibold text-charcoal-900 mb-1">Your Decision:</p>
            <p className="text-charcoal-700">{currentStep.prompt}</p>
          </div>

          {/* Options */}
          <div className="space-y-3 mb-6">
            {currentStep.options.map((option) => {
              const isSelected = selectedOption === option.id;
              const isDisabled = showFeedback;

              return (
                <button
                  key={option.id}
                  onClick={() => !isDisabled && handleSelectOption(option.id)}
                  disabled={isDisabled}
                  className={`
                    w-full text-left p-4 rounded-lg border-2 transition-all
                    ${isSelected
                      ? 'border-primary-500 bg-primary-50 shadow-md'
                      : 'border-charcoal-200 bg-white hover:border-primary-300 hover:bg-charcoal-50'
                    }
                    ${isDisabled ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                >
                  <div className="flex items-start gap-3">
                    <div className={`
                      flex-shrink-0 w-5 h-5 rounded-full border-2 mt-0.5 flex items-center justify-center
                      ${isSelected ? 'border-primary-600 bg-primary-600' : 'border-charcoal-300'}
                    `}>
                      {isSelected && <div className="w-2 h-2 bg-white rounded-full" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-charcoal-900 mb-1">{option.text}</p>
                      {isSelected && !showFeedback && (
                        <p className="text-sm text-charcoal-600 italic">
                          {option.consequence}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Feedback */}
          {showFeedback && currentFeedback && (
            <div className={`
              border-2 rounded-lg p-6 mb-6 animate-fadeIn
              ${currentFeedback.rating === 'excellent' ? 'border-sage-400 bg-sage-50'
                : currentFeedback.rating === 'good' ? 'border-primary-400 bg-primary-50'
                : currentFeedback.rating === 'okay' ? 'border-amber-400 bg-amber-50'
                : 'border-coral-400 bg-coral-50'
              }
            `}>
              <div className="flex items-start gap-3 mb-4">
                <Lightbulb className={`h-6 w-6 flex-shrink-0 ${
                  currentFeedback.rating === 'excellent' || currentFeedback.rating === 'good'
                    ? 'text-sage-700'
                    : 'text-amber-700'
                }`} />
                <div>
                  <h4 className="font-bold text-charcoal-900 text-lg mb-2">
                    {currentFeedback.message}
                  </h4>
                  <p className="text-charcoal-700 mb-3 leading-relaxed">
                    {currentFeedback.explanation}
                  </p>
                  {currentFeedback.tips && currentFeedback.tips.length > 0 && (
                    <div className="mt-3">
                      <p className="font-semibold text-charcoal-800 mb-2 text-sm">Key Takeaways:</p>
                      <ul className="space-y-1">
                        {currentFeedback.tips.map((tip, idx) => (
                          <li key={idx} className="text-sm text-charcoal-700 flex items-start gap-2">
                            <span className="text-sage-600 flex-shrink-0">•</span>
                            <span>{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between">
            <button
              onClick={handleRestart}
              className="text-sm text-charcoal-600 hover:text-charcoal-900 underline"
            >
              Restart Simulation
            </button>
            {!showFeedback ? (
              <button
                onClick={handleSubmit}
                disabled={!selectedOption}
                className="flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-charcoal-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
              >
                Submit Answer
                <ArrowRight className="h-5 w-5" />
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="flex items-center gap-2 px-6 py-3 bg-sage-600 hover:bg-sage-700 text-white font-semibold rounded-lg transition-colors"
              >
                {currentStepIndex < totalSteps - 1 ? 'Next Stage' : 'Complete'}
                <ArrowRight className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

